/**
 * Payment orders — the view the product did not have.
 *
 * Distinct from the Orders page, which lists **commerce** orders (SPU/SKU
 * shopping orders). Those are different tables: during the 2026-08-21
 * acceptance run three real PayKKa payments, a wallet top-up and three refunds
 * all existed and were correct while Orders and every dashboard metric read
 * zero, because nothing in the product could enumerate a payment.
 *
 * Everything here comes from `GET /api/v1/payments`. There is no seeded or
 * illustrative data: an empty table means the system has no matching payment.
 *
 * The list is tenant-scoped: with a user token the backend needs
 * `X-Application-ID`, so the operator picks an application first. Without it
 * the API answers 401 — which this page used to swallow as an empty list.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, ApiRequestError } from '@/lib/api'
import {
  readStoredApplicationId,
  resolveInitialSelection,
  resolveListView,
  tenantHeaders,
  writeStoredApplicationId,
  type ApplicationOption,
} from '@/pages/payments/applicationContext'

interface PaymentRow {
  order_id: string
  status: string
  channel_type: string
  channel_order_id: string | null
  amount: number
  currency: string
  description: string | null
  refunded_amount: number
  created_at: string | null
  paid_at: string | null
}

interface Pagination {
  page: number
  per_page: number
  total: number
  pages: number
}

/** Enough to list every application an operator owns today; the selector is not paginated. */
const APPLICATIONS_PAGE_SIZE = 100

const STATUSES = [
  'pending', 'processing', 'paid', 'failed', 'expired',
  'refunding', 'partial_refunded', 'refunded',
]

/** Minor units → a readable amount. The API is always in the currency's minor unit. */
function formatAmount(minor: number, currency: string): string {
  const zeroDecimal = ['JPY', 'KRW', 'VND']
  const divisor = zeroDecimal.includes(currency) ? 1 : 100
  return `${(minor / divisor).toFixed(divisor === 1 ? 0 : 2)} ${currency}`
}

function statusTone(status: string): string {
  if (status === 'paid') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  if (status === 'refunded' || status === 'partial_refunded')
    return 'bg-violet-50 text-violet-700 border-violet-200'
  if (status === 'refunding' || status === 'processing' || status === 'pending')
    return 'bg-amber-50 text-amber-700 border-amber-200'
  return 'bg-red-50 text-red-700 border-red-200'
}

export default function PaymentsPage() {
  // `?order_id=` lets another surface navigate to one payment order — the
  // Accounts console's top-up ledger rows do, since a payment order has no
  // detail route of its own and the commerce Orders detail is a different table.
  const [searchParams] = useSearchParams()
  const [rows, setRows] = useState<PaymentRow[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [status, setStatus] = useState('')
  const [channelType, setChannelType] = useState('')
  const [search, setSearch] = useState(() => searchParams.get('order_id') ?? '')
  const [page, setPage] = useState(1)
  const [channels, setChannels] = useState<string[]>([])
  const [applications, setApplications] = useState<ApplicationOption[]>([])
  const [applicationsLoaded, setApplicationsLoaded] = useState(false)
  const [applicationId, setApplicationId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const storage = typeof window === 'undefined' ? null : window.localStorage
  // Monotonic request id: a late response from an earlier application/filter
  // must never overwrite the rows of the current one.
  const requestSeq = useRef(0)

  const selectApplication = useCallback((id: number | null) => {
    setApplicationId(id)
    setPage(1)
    setError(null)
    setLoaded(false)
    setRows([])
    setPagination(null)
    writeStoredApplicationId(storage, id)
  }, [storage])

  useEffect(() => {
    api
      .get<ApplicationOption[]>('/applications', { per_page: APPLICATIONS_PAGE_SIZE })
      .then((res) => {
        const apps = Array.isArray(res.data) ? res.data : []
        setApplications(apps)
        const initial = resolveInitialSelection(readStoredApplicationId(storage), apps)
        setApplicationId(initial)
        writeStoredApplicationId(storage, initial)
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiRequestError ? err.error.message : 'Failed to load applications')
      })
      .finally(() => setApplicationsLoaded(true))
  }, [storage])

  const load = useCallback(async () => {
    if (applicationId == null) return
    const seq = ++requestSeq.current
    const isCurrent = () => seq === requestSeq.current
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, string | number> = { page, per_page: 20 }
      if (status) params.status = status
      if (channelType) params.channel_type = channelType
      if (search.trim()) params.search = search.trim()

      const res = await api.get<PaymentRow[]>('/payments', params, tenantHeaders(applicationId))
      if (!isCurrent()) return
      setRows(Array.isArray(res.data) ? res.data : [])
      setPagination(res.pagination ?? null)
      setLoaded(true)
    } catch (err: unknown) {
      if (!isCurrent()) return
      // Keep the error visible; do NOT present a failed request as "no payments".
      setError(err instanceof ApiRequestError ? err.error.message : 'Failed to load payments')
      setLoaded(false)
    } finally {
      if (isCurrent()) setLoading(false)
    }
  }, [applicationId, page, status, channelType, search])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    api
      .get<{ channels?: { channel_type?: string }[] }>('/payments/channels')
      .then((res) => {
        const names = (res.data?.channels ?? [])
          .map((c) => c.channel_type)
          .filter((c): c is string => Boolean(c))
        setChannels(names)
      })
      .catch(() => setChannels([]))
  }, [])

  const view = resolveListView({
    loading: loading || !applicationsLoaded,
    error,
    selectedApplicationId: applicationId,
    rowCount: rows.length,
    loaded,
  })

  return (
    <main className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Payments</h1>
        <p className="text-sm text-muted-foreground">
          Payment orders and their refund state. Separate from{' '}
          <strong>Orders</strong>, which lists commerce (product) orders — a payment
          here does not create a row there.
        </p>
      </header>

      {error && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <section aria-labelledby="filters-heading" className="rounded-lg border p-5">
        <h2 id="filters-heading" className="mb-4 text-lg font-medium">Filters</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Application</span>
            <select
              className="w-full rounded-md border px-3 py-2"
              value={applicationId ?? ''}
              onChange={(e) => selectApplication(e.target.value ? Number(e.target.value) : null)}
              aria-label="Application"
            >
              <option value="">Select an application</option>
              {applications.map((a) => (
                <option key={a.id} value={a.id}>{a.name} (#{a.id})</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Status</span>
            <select
              className="w-full rounded-md border px-3 py-2"
              value={status}
              onChange={(e) => { setPage(1); setStatus(e.target.value) }}
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Channel</span>
            <select
              className="w-full rounded-md border px-3 py-2"
              value={channelType}
              onChange={(e) => { setPage(1); setChannelType(e.target.value) }}
            >
              <option value="">All channels</option>
              {channels.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block text-muted-foreground">Search</span>
            <input
              className="w-full rounded-md border px-3 py-2"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load() } }}
              placeholder="order id, or start of the description"
            />
          </label>
        </div>
      </section>

      <section aria-labelledby="table-heading" className="rounded-lg border p-5">
        <h2 id="table-heading" className="mb-4 text-lg font-medium">
          All Payments{pagination ? ` (${pagination.total})` : ''}
        </h2>

        {view === 'error' ? (
          <p className="text-sm text-red-700">
            Could not load payments — see the error above.
          </p>
        ) : view === 'select-application' ? (
          <p className="text-sm text-muted-foreground">
            Select an application to list its payments. Payments are scoped per application.
          </p>
        ) : view === 'loading' ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : view === 'empty' ? (
          <p className="text-sm text-muted-foreground">
            No payments match these filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Order</th>
                  <th className="py-2 pr-4 font-medium">Amount</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Refunded</th>
                  <th className="py-2 pr-4 font-medium">Channel</th>
                  <th className="py-2 pr-4 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.order_id} className="border-b last:border-0 transition-colors hover:bg-slate-50">
                    <td className="py-2 pr-4">
                      <div className="font-mono text-xs">{row.order_id}</div>
                      {row.description && (
                        <div className="text-xs text-muted-foreground">{row.description}</div>
                      )}
                    </td>
                    <td className="py-2 pr-4 font-medium">{formatAmount(row.amount, row.currency)}</td>
                    <td className="py-2 pr-4">
                      <span className={`inline-block rounded-md border px-2 py-0.5 text-xs ${statusTone(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4">
                      {row.refunded_amount > 0 ? formatAmount(row.refunded_amount, row.currency) : '—'}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">{row.channel_type}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.pages > 1 && (
          <div className="mt-4 flex items-center gap-3 text-sm">
            <button
              className="rounded-md border px-3 py-1 transition-colors hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span className="text-muted-foreground">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              className="rounded-md border px-3 py-1 transition-colors hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              disabled={page >= pagination.pages}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </main>
  )
}
